import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  Tabs,
  Tab,
  AlertTitle,
  Grid,
  IconButton,
  Tooltip,
  Divider,
  TextField,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Backdrop,
  useMediaQuery,
  useTheme,
  Collapse,
  Fab,
  SwipeableDrawer,
  AppBar,
  Toolbar,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Stack,
  Container
} from '@mui/material';
import {
  School as SchoolIcon,
  Assessment as AssessmentIcon,
  Assignment as AssignmentIcon,
  Refresh as RefreshIcon,
  LogoutOutlined,
  Article as ArticleIcon,
  Description as DescriptionIcon,
  Chat as ChatIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  ThumbUp as ApproveIcon,
  ThumbDown as RejectIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Event as EventIcon,
  Grade as GradeIcon,
  PlayArrow as PlayArrowIcon,
  Send as SendIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  FilterList as FilterListIcon,
  Sort as SortIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';

// Serviço que unifica as chamadas do dashboard do supervisor
import SupervisorDashboardService from '../../services/SupervisorDashboardService';
import DashboardHeader from '../../components/Dashboard/DashboardHeader';

// SERVIÇOS ATUALIZADOS PARA MONOGRAFIAS
import {
  listarMonografiasPorSupervisor,
  marcarMonografiaEmAnalise,
  solicitarCorrecoes,
  aprovarMonografiaPeloSupervisor,
  getStatusDisplayName,
  getStatusColor,
  getAvailableActions,
  obterHistoricoMonografia,
  obterEstatisticasVersionamento,
  obterVersaesDocumentos,
  canUserPerformAction
} from '../../services/monografiaService';

import api from '../../services/api';

// Componentes de listagem (mantendo os originais)
import PropostaList from '../../components/Proposta/PropostaList';
import ProjetoList from '../../components/Projeto/ProjetoList';
import MonografiaList from '../../components/Monografia/MonografiaList';
import DefesaList from '../../components/Defesa/DefesaList';
import AvaliacaoList from '../../components/Avaliacao/AvaliacaoList';
import AvaliacoesPendentes from '../../components/Avaliacao/AvaliacoesPendentes';

// Componente de chat aprimorado
import ChatDialog from '../../components/ChatDialog';

// Hooks de autenticação e notificações
import useAuth from '../../hooks/useAuth';
import { useNotificacao } from '../../contexts/NotificacaoContext';
import { useRole } from '../../contexts/RoleContext';


// Outros componentes
import Footer from '../../components/Footer/Footer';

// COMPONENTE DE NOTIFICAÇÃO CUSTOMIZADO
const NotificationSnackbar = ({ open, message, severity, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const getIcon = () => {
    switch (severity) {
      case 'success': return <CheckCircleIcon />;
      case 'error': return <ErrorIcon />;
      case 'warning': return <WarningIcon />;
      case 'info': return <InfoIcon />;
      default: return <InfoIcon />;
    }
  };

  return (
    <Snackbar
      open={open}
      autoHideDuration={6000}
      onClose={onClose}
      anchorOrigin={{ 
        vertical: isMobile ? 'bottom' : 'top', 
        horizontal: isMobile ? 'center' : 'right' 
      }}
    >
      <Alert 
        onClose={onClose} 
        severity={severity} 
        sx={{ 
          width: '100%',
          minWidth: isMobile ? '300px' : '350px',
          fontSize: '14px',
          '& .MuiAlert-icon': {
            fontSize: '20px'
          }
        }}
        icon={getIcon()}
      >
        {message}
      </Alert>
    </Snackbar>
  );
};

NotificationSnackbar.propTypes = {
  open: PropTypes.bool.isRequired,
  message: PropTypes.string.isRequired,
  severity: PropTypes.oneOf(['success', 'error', 'warning', 'info']).isRequired,
  onClose: PropTypes.func.isRequired
};

// FUNÇÃO DE TRADUÇÃO
const traduzirAcao = (acao) => {
  const traducoes = {
    'SUBMISSAO_INICIAL': '🎯 Submissão Inicial',
    'SUPERVISOR_APROVACAO': '✅ Aprovação pelo Supervisor', 
    'APROVACAO_SUPERVISOR': '✅ Supervisor Aprovou',
    'SUPERVISOR_CORRECOES': '📝 Correções Solicitadas',
    'RESUBMISSAO': '🔄 Resubmissão pelo Estudante',
    'SUBMISSAO_DIRECAO': '🏛️ Submetida à Direção Científica',
    'APROVACAO_FINAL': '🏆 Aprovação Final',
    'DEVOLUCAO_DIRECAO': '↩️ Devolvida pela Direção'
  };
  return traducoes[acao] || acao.replace(/_/g, ' ');
};

// COMPONENTE MOBILE DE CARD COLAPSÍVEL
const MobileCard = ({ children, title, status, isNew, actions, ...props }) => {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <Card 
      sx={{ 
        mb: 2,
        border: isNew ? '2px solid #2196f3' : '1px solid rgba(0,0,0,0.12)',
        boxShadow: isNew ? '0 4px 8px rgba(33, 150, 243, 0.3)' : undefined
      }}
      {...props}
    >
      <CardContent sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box sx={{ flex: 1, pr: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="h6" sx={{ fontSize: '1rem', lineHeight: 1.2 }}>
                {title}
              </Typography>
              {isNew && (
                <Chip
                  label="NOVO"
                  size="small"
                  color="primary"
                  sx={{ 
                    fontWeight: 'bold',
                    fontSize: '0.7rem',
                    height: '20px'
                  }}
                />
              )}
            </Box>
            {status && (
              <Chip
                label={status}
                size="small"
                sx={{ mb: 1 }}
              />
            )}
          </Box>
          
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
            sx={{ mt: -1 }}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          {children}
        </Collapse>
      </CardContent>
      
      {actions && (
        <CardActions sx={{ pt: 0, px: 2, pb: 2, flexDirection: 'column', gap: 1 }}>
          <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            flexWrap: 'wrap', 
            width: '100%',
            '& .MuiButton-root': {
              minWidth: 'unset',
              px: 2,
              py: 0.5,
              fontSize: '0.75rem'
            }
          }}>
            {actions}
          </Box>
        </CardActions>
      )}
    </Card>
  );
};

MobileCard.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  status: PropTypes.string,
  isNew: PropTypes.bool,
  actions: PropTypes.node
};

const SupervisorDashboardPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Estados para controle das abas e dos dados do dashboard
  const [activeTab, setActiveTab] = useState(0);
  const [dashboardData, setDashboardData] = useState({
    propostas: [],
    projetos: [],
    monografias: [],
    defesas: [],
    avaliacoes: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // NOVO ESTADO para upload de arquivo
  const [arquivoCorrecao, setArquivoCorrecao] = useState(null);
  const [previewArquivo, setPreviewArquivo] = useState('');

  // Estados para o chat
  const [chatDialogOpen, setChatDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [chatContext, setChatContext] = useState("projeto");
  const [chatMessage, setChatMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [loadingChat, setLoadingChat] = useState(false);

  // Estados para ações avançadas
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState('');
  const [selectedMonografia, setSelectedMonografia] = useState(null);
  const [selectedDefesa, setSelectedDefesa] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Estados para avaliação
  const [avaliacaoDialog, setAvaliacaoDialog] = useState(false);
  const [avaliacaoData, setAvaliacaoData] = useState({
    notaTrabalhoEscrito: '',
    notaDefesa: '',
    observacoes: ''
  });

  // Estados para agendamento de defesa
  const [defesaDialog, setDefesaDialog] = useState(false);
  const [defesaData, setDefesaData] = useState({
    dataDefesa: '',
    localDefesa: '',
    presidenteId: '',
    arguenteId: ''
  });

  // ESTADOS PARA O FLUXO DE MONOGRAFIAS
  const [analiseDialogOpen, setAnaliseDialogOpen] = useState(false);
  const [correcoesDialogOpen, setCorrecoesDialogOpen] = useState(false);
  const [aprovacaoDialogOpen, setAprovacaoDialogOpen] = useState(false);
  const [dadosCorrecoes, setDadosCorrecoes] = useState({
    observacoes: '',
    prazoCorrecao: ''
  });
  const [observacoesAprovacao, setObservacoesAprovacao] = useState('');

  // Estados para observações/feedback
  const [observacoesDialog, setObservacoesDialog] = useState(false);
  const [observacoes, setObservacoes] = useState('');

  // Estados para notificações
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  const [historicoDialogOpen, setHistoricoDialogOpen] = useState(false);
  const [versaoDialogOpen, setVersaoDialogOpen] = useState(false);
  const [historicoData, setHistoricoData] = useState(null);
  const [versaoData, setVersaoData] = useState([]);

  // Estados para mobile
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [speedDialOpen, setSpeedDialOpen] = useState(false);

  // Hooks de autenticação e navegação
  const { user, logout } = useAuth();
  const { naoLidas, loading: notificacoesLoading, fetchNotificacoes } = useNotificacao();
  const navigate = useNavigate();


  const { currentRole } = useRole();
const effectiveRole = currentRole && currentRole !== 'auto' ? currentRole : (user?.tipo || '');

  const handleAbrirHistorico = async (monografia) => {
    try {
      showNotification('🔍 Carregando histórico...', 'info');
      
      setSelectedMonografia(monografia);
      setHistoricoData(null);
      setHistoricoDialogOpen(true);
      
      const response = await obterHistoricoMonografia(monografia.id);
      
      if (!response || !response.data) {
        throw new Error('Resposta da API inválida');
      }
      
      const dadosHistorico = response.data;
      
      if (!dadosHistorico.timeline) {
        dadosHistorico.timeline = [];
      }
      
      if (!dadosHistorico.estatisticas) {
        dadosHistorico.estatisticas = {
          total_acoes: 0,
          numero_resubmissoes: 0
        };
      }
      
      setHistoricoData(dadosHistorico);
      showNotification('✅ Histórico carregado!', 'success');
      
    } catch (error) {
      console.error('❌ Erro ao carregar histórico:', error);
      setHistoricoDialogOpen(false);
      setHistoricoData(null);
      handleApiError(error, 'carregamento do histórico');
    }
  };

  // FUNÇÃO para lidar com seleção de arquivo
  const handleArquivoCorrecaoChange = (event) => {
    const arquivo = event.target.files[0];
    
    if (!arquivo) {
      setArquivoCorrecao(null);
      setPreviewArquivo('');
      return;
    }

    const tiposPermitidos = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword'
    ];
    
    if (!tiposPermitidos.includes(arquivo.type)) {
      showNotification('❌ Tipo de arquivo não permitido. Use PDF ou Word (.doc/.docx)', 'error');
      event.target.value = '';
      return;
    }

    const tamanhoMaximo = 10 * 1024 * 1024;
    if (arquivo.size > tamanhoMaximo) {
      showNotification('❌ Arquivo muito grande. Máximo permitido: 10MB', 'error');
      event.target.value = '';
      return;
    }

    setArquivoCorrecao(arquivo);
    setPreviewArquivo(`📎 ${arquivo.name} (${(arquivo.size / 1024 / 1024).toFixed(2)} MB)`);
    showNotification('✅ Arquivo selecionado com sucesso!', 'success');
  };

  const removerArquivoCorrecao = () => {
    setArquivoCorrecao(null);
    setPreviewArquivo('');
    const fileInput = document.getElementById('arquivo-correcao-input');
    if (fileInput) fileInput.value = '';
    showNotification('🗑️ Arquivo removido', 'info');
  };

  // FUNÇÃO PARA MOSTRAR NOTIFICAÇÕES
  const showNotification = (message, severity = 'info') => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  // FUNÇÃO PARA FECHAR NOTIFICAÇÕES
  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  // FUNÇÃO MELHORADA PARA TRATAMENTO DE ERROS DE API
  const handleApiError = (error, operacao = 'operação') => {
    console.error(`Erro na ${operacao}:`, error);
    
    let mensagem = '';
    
    if (error.response) {
      const status = error.response.status;
      const data = error.response.data;
      
      switch (status) {
        case 400:
          mensagem = `⚠️ ${data.message || 'Dados inválidos'}`;
          break;
        case 401:
          mensagem = `🔐 Sessão expirada. Por favor, faça login novamente`;
          break;
        case 403:
          mensagem = `🚫 Você não tem permissão para realizar esta operação`;
          break;
        case 404:
          mensagem = `🔍 Recurso não encontrado`;
          break;
        case 422:
          if (data.errors && Array.isArray(data.errors)) {
            mensagem = `📝 Erros de validação:\n${data.errors.map(err => `• ${err.message || err}`).join('\n')}`;
          } else {
            mensagem = `📝 ${data.message || 'Dados inválidos'}`;
          }
          break;
        case 500:
          mensagem = `🔧 Erro interno do servidor. Tente novamente em alguns minutos`;
          break;
        default:
          mensagem = `❌ Erro inesperado (${status}). Tente novamente`;
      }
    } else if (error.request) {
      mensagem = `🌐 Erro de conexão. Verifique sua internet e tente novamente`;
    } else {
      mensagem = `❌ Erro inesperado: ${error.message}`;
    }
    
    showNotification(mensagem, 'error');
    return mensagem;
  };

  // FUNÇÕES DE DOWNLOAD E VISUALIZAÇÃO
  const handleDownloadDocument = async (documento, tipo) => {
    try {
      showNotification('🔄 Iniciando download do documento...', 'info');
      
      let fileUrl = null;
      let fileName = null;

      if (tipo === 'projeto') {
        fileUrl = documento.documento_projeto;
        fileName = `projeto_${documento.id}_${documento.proposta?.titulo?.substring(0, 20) || 'documento'}`;
      } else if (tipo === 'monografia') {
        fileUrl = documento.documento_monografia || documento.arquivo_url || documento.documento_url;
        fileName = `monografia_${documento.id}_${(documento.titulo || documento.projeto?.proposta?.titulo)?.substring(0, 20) || 'documento'}`;
      } else if (tipo === 'proposta') {
        fileUrl = documento.documento_proposta || documento.arquivo_url;
        fileName = `proposta_${documento.id}_${documento.titulo?.substring(0, 20) || 'documento'}`;
      }

      if (!fileUrl) {
        showNotification('❌ Documento não encontrado para download.', 'warning');
        return;
      }

      const fileNameFromPath = fileUrl.split('\\').pop().split('/').pop();
      const apiUrl = `http://localhost:5000/api/documentos/${fileNameFromPath}`;

      const token = localStorage.getItem('token');
      if (!token) {
        showNotification('❌ Sessão expirada. Faça login novamente.', 'error');
        return;
      }

      showNotification('⏳ Processando download...', 'info');

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      const blob = await response.blob();
      const originalExtension = fileNameFromPath.split('.').pop();
      const finalFileName = `${fileName}.${originalExtension}`;

      const objectUrl = URL.createObjectURL(blob);
      const downloadLink = document.createElement('a');
      downloadLink.href = objectUrl;
      downloadLink.download = finalFileName;
      downloadLink.style.display = 'none';
      
      document.body.appendChild(downloadLink);
      downloadLink.click();
      
      setTimeout(() => {
        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(objectUrl);
      }, 100);

      showNotification(`✅ Download concluído: ${finalFileName}`, 'success');

    } catch (error) {
      handleApiError(error, 'download do documento');
    }
  };

  const handleViewDocument = async (documento, tipo) => {
    try {
      showNotification('🔍 Abrindo documento...', 'info');
        
      let fileUrl = null;
      
      if (tipo === 'projeto') {
        fileUrl = documento.documento_projeto;
      } else if (tipo === 'monografia') {
        fileUrl = documento.documento_monografia || documento.arquivo_url || documento.documento_url;
      } else if (tipo === 'proposta') {
        fileUrl = documento.documento_proposta || documento.arquivo_url;
      }

      if (!fileUrl) {
        showNotification('❌ Documento não disponível para visualização.', 'warning');
        return;
      }

      const fileNameFromPath = fileUrl.split('\\').pop().split('/').pop();
      const fileExtension = fileNameFromPath.split('.').pop().toLowerCase();
      const publicUrl = `http://localhost:5000/api/documentos/view/${fileNameFromPath}`;

      if (fileExtension === 'pdf') {
        window.open(publicUrl, '_blank');
        showNotification('📄 Documento PDF aberto em nova aba!', 'success');
      } else if (['docx', 'doc', 'xlsx', 'xls', 'pptx', 'ppt'].includes(fileExtension)) {
        const userChoice = window.confirm(
          `📄 Documento: ${fileNameFromPath}\n\n` +
          `Os documentos Word/Excel não podem ser visualizados diretamente no navegador.\n\n` +
          `Clique OK para baixar e abrir no seu computador.\n` +
          `Clique Cancelar para tentar visualizar online (pode não funcionar).`
        );
        
        if (userChoice) {
          handleDownloadDocument(documento, tipo);
        } else {
          const googleViewerUrl = `https://docs.google.com/gviewer?url=${encodeURIComponent(publicUrl)}&embedded=true`;
          window.open(googleViewerUrl, '_blank', 'width=1200,height=800');
          showNotification('🌐 Tentando visualizar documento online...', 'info');
        }
      } else {
        window.open(publicUrl, '_blank');
        showNotification('📄 Documento aberto em nova aba!', 'success');
      }

    } catch (error) {
      console.error('❌ Erro ao visualizar documento:', error);
      showNotification(`❌ Erro ao abrir documento: ${error.message}`, 'error');
    }
  };

  // FUNÇÕES PARA VERIFICAR ITENS NOVOS E ORDENAÇÃO
  const isNewItem = (item, tipo) => {
    let dataSubmissao = null;
    
    if (tipo === 'proposta') {
      dataSubmissao = item.created_at || item.data_submissao;
    } else if (tipo === 'projeto') {
      dataSubmissao = item.created_at || item.data_submissao;
    } else if (tipo === 'monografia') {
      dataSubmissao = item.created_at || item.data_submissao || item.data_resubmissao;
    } else if (tipo === 'defesa') {
      dataSubmissao = item.created_at || item.data_agendamento;
    }
    
    if (!dataSubmissao) return false;
    
    const agora = new Date();
    const dataItem = new Date(dataSubmissao);
    const diferencaHoras = (agora - dataItem) / (1000 * 60 * 60);
    
    return diferencaHoras <= 48;
  };

  const sortItemsByDate = (items, tipo) => {
    return [...items].sort((a, b) => {
      let dataA = null;
      let dataB = null;
      
      if (tipo === 'proposta') {
        dataA = a.created_at || a.data_submissao;
        dataB = b.created_at || b.data_submissao;
      } else if (tipo === 'projeto') {
        dataA = a.created_at || a.data_submissao;
        dataB = b.created_at || b.data_submissao;
      } else if (tipo === 'monografia') {
        dataA = a.data_resubmissao || a.created_at || a.data_submissao;
        dataB = b.data_resubmissao || b.created_at || b.data_submissao;
      } else if (tipo === 'defesa') {
        dataA = a.data_defesa || a.created_at;
        dataB = b.data_defesa || b.created_at;
      }
      
      if (!dataA && !dataB) return 0;
      if (!dataA) return 1;
      if (!dataB) return -1;
      
      return new Date(dataB) - new Date(dataA);
    });
  };

  // FUNÇÃO PARA BUSCAR DADOS DO DASHBOARD
  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      showNotification('🔄 Carregando dados do painel...', 'info');
      
      let monografiasData = [];
      try {
        const monografiasResponse = await listarMonografiasPorSupervisor();
        monografiasData = monografiasResponse.data || [];
      } catch (monografiaError) {
        console.error('❌ Erro ao carregar monografias:', monografiaError);
        handleApiError(monografiaError, 'carregamento das monografias');
      }
      
      let dashboardDataGeneral = {
        propostas: [],
        projetos: [],
        defesas: [],
        avaliacoes: []
      };
      
      try {
        const tempData = await SupervisorDashboardService.getSupervisaoData();
        dashboardDataGeneral = {
          propostas: tempData.propostas || [],
          projetos: tempData.projetos || [],
          defesas: tempData.defesas || [],
          avaliacoes: tempData.avaliacoes || []
        };
      } catch (dashboardError) {
        console.error('❌ Erro ao carregar dados gerais:', dashboardError);
        handleApiError(dashboardError, 'carregamento dos dados gerais');
      }
      
      const combinedData = {
        ...dashboardDataGeneral,
        monografias: monografiasData
      };
      
      setDashboardData(combinedData);
      setError(null);
      showNotification('✅ Dados carregados com sucesso!', 'success');
    } catch (err) {
      console.error('Erro ao carregar dados do dashboard:', err);
      const errorMessage = handleApiError(err, 'carregamento dos dados');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

useEffect(() => {
  if (effectiveRole === 'supervisor') {
    fetchDashboardData();
  }
}, [effectiveRole, fetchDashboardData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchDashboardData();
    showNotification('✅ Lista atualizada!', 'info');
    setRefreshing(false);
  };

  useEffect(() => {
    if (user?.tipo === 'supervisor') {
      fetchDashboardData();
    }
  }, [user, fetchDashboardData]);

  useEffect(() => {
  let interval;
  if (activeTab === 2 && effectiveRole === 'supervisor') {
    interval = setInterval(() => {
      fetchDashboardData();
    }, 120000);
  }

  return () => {
    if (interval) clearInterval(interval);
  };
}, [activeTab, fetchDashboardData, effectiveRole]);


  // FUNÇÕES PARA O CHAT
  const openChatDialogHandler = async (item, context) => {
    setSelectedItem(item);
    setChatContext(context);
    setChatDialogOpen(true);
    setLoadingChat(true);
    showNotification('💬 Carregando histórico de mensagens...', 'info');
    
    try {
      const response = await api.get('/chat/messages', {
        params: { context, context_id: item.id }
      });
      setChatHistory(response.data);
      showNotification('✅ Chat carregado com sucesso!', 'success');
    } catch (error) {
      console.error("Erro ao carregar histórico de chat:", error);
      handleApiError(error, 'carregamento do chat');
    } finally {
      setLoadingChat(false);
    }
  };

  const closeChatDialogHandler = () => {
    setChatDialogOpen(false);
    setSelectedItem(null);
    setChatMessage('');
    setChatHistory([]);
    showNotification('💬 Chat fechado', 'info');
  };

  const sendChatMessageHandler = async () => {
    if (chatMessage.trim() === '') {
      showNotification('⚠️ Digite uma mensagem antes de enviar', 'warning');
      return;
    }
    
    const payload = {
      context: chatContext,
      context_id: selectedItem.id,
      sender: user?.nome,
      message: chatMessage
    };
    
    showNotification('📤 Enviando mensagem...', 'info');
    
    try {
      await api.post('/chat/messages', payload);
      setChatMessage('');
      const response = await api.get('/chat/messages', {
        params: { context: chatContext, context_id: selectedItem.id }
      });
      setChatHistory(response.data);
      showNotification('✅ Mensagem enviada com sucesso!', 'success');
    } catch (error) {
      console.error("Erro ao enviar mensagem:", error);
      handleApiError(error, 'envio da mensagem');
    }
  };

  // NOVAS FUNÇÕES PARA AÇÕES EM MONOGRAFIAS
  const handleMonografiaAction = (monografia, action) => {
    setSelectedMonografia(monografia);
    setActionType(action);
    
    switch (action) {
      case 'marcar-em-analise':
        setAnaliseDialogOpen(true);
        break;
      case 'solicitar-correcoes':
        setCorrecoesDialogOpen(true);
        setDadosCorrecoes({ observacoes: '', prazoCorrecao: '' });
        break;
      case 'aprovar':
        setAprovacaoDialogOpen(true);
        setObservacoesAprovacao('');
        break;
      default:
        console.warn('Ação não reconhecida:', action);
    }
  };

  // Executar ação específica de monografia
  const executeMonografiaAction = async () => {
    if (!selectedMonografia || !actionType) {
      showNotification('❌ Erro: dados da ação não encontrados', 'error');
      return;
    }
    
    setActionLoading(true);
    
    try {
      let response;
      let successMessage = '';
      
      switch (actionType) {
        case 'marcar-em-analise':
          showNotification('🔄 Marcando monografia como em análise...', 'info');
          response = await marcarMonografiaEmAnalise(selectedMonografia.id);
          successMessage = '✅ Monografia marcada como Em Análise com sucesso!';
          break;
          
        case 'solicitar-correcoes':
          if (!dadosCorrecoes.observacoes || dadosCorrecoes.observacoes.trim().length < 10) {
            showNotification('❌ Observações devem ter pelo menos 10 caracteres', 'error');
            setActionLoading(false);
            return;
          }
          
          showNotification('📝 Solicitando correções...', 'info');
          
          const formData = new FormData();
          formData.append('observacoes', dadosCorrecoes.observacoes);
          
          if (dadosCorrecoes.prazoCorrecao) {
            formData.append('prazo_correcao', dadosCorrecoes.prazoCorrecao);
          }
          
          if (arquivoCorrecao) {
            formData.append('documento_corrigido', arquivoCorrecao);
            showNotification('📎 Anexando documento corrigido...', 'info');
          }
          
          try {
            response = await api.put(`/monografias/${selectedMonografia.id}/solicitar-correcoes`, formData, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            });
            
            successMessage = arquivoCorrecao ? 
              '✅ Correções solicitadas com documento anexado!' : 
              '✅ Correções solicitadas com sucesso!';
              
            if (response.data.documento_corrigido) {
              showNotification(`📎 Documento corrigido salvo: ${response.data.documento_corrigido}`, 'success');
            }
            
          } catch (error) {
            console.error('Erro ao solicitar correções:', error);
            throw error;
          }
          
          break;
        
        case 'aprovar':
          showNotification('✅ Aprovando monografia...', 'info');
          response = await aprovarMonografiaPeloSupervisor(
            selectedMonografia.id,
            observacoesAprovacao
          );
          successMessage = '🎉 Monografia aprovada pelo supervisor!';
          break;
          
        default:
          throw new Error(`Ação não implementada: ${actionType}`);
      }
      
      showNotification(successMessage, 'success');
      await fetchDashboardData();
      
      setAnaliseDialogOpen(false);
      setCorrecoesDialogOpen(false);
      setAprovacaoDialogOpen(false);
      
      setSelectedMonografia(null);
      setActionType('');
      setDadosCorrecoes({ observacoes: '', prazoCorrecao: '' });
      setObservacoesAprovacao('');

      setArquivoCorrecao(null);
      setPreviewArquivo('');
      const fileInput = document.getElementById('arquivo-correcao-input');
      if (fileInput) fileInput.value = '';

    } catch (error) {
      console.error(`Erro ao executar ação ${actionType}:`, error);
      handleApiError(error, `execução da ação ${actionType}`);
    } finally {
      setActionLoading(false);
    }
  };

  // FUNÇÕES PARA DEFESAS
  const handleDefesaAction = (defesa, action) => {
    setSelectedDefesa(defesa);
    setActionType(action);
    
    if (action === 'agendar') {
      setDefesaDialog(true);
    } else if (action === 'avaliar') {
      setAvaliacaoDialog(true);
    }
  };

  const executeDefesaAction = async () => {
    if (!selectedDefesa || !actionType) return;
    
    setActionLoading(true);
    
    try {
      let endpoint = '';
      let payload = {};
      let successMessage = '';
      
      if (actionType === 'agendar') {
        endpoint = `/defesas/${selectedDefesa.id}/agendar`;
        payload = defesaData;
        successMessage = 'Defesa agendada com sucesso!';
        showNotification('📅 Agendando defesa...', 'info');
      } else if (actionType === 'avaliar') {
        endpoint = `/defesas/${selectedDefesa.id}/avaliar`;
        payload = {
          ...avaliacaoData,
          mediaFinal: ((parseFloat(avaliacaoData.notaTrabalhoEscrito) + parseFloat(avaliacaoData.notaDefesa)) / 2).toFixed(2)
        };
        successMessage = 'Avaliação registrada com sucesso!';
        showNotification('📊 Registrando avaliação...', 'info');
      }
      
      await api.post(endpoint, payload);
      
      showNotification(`✅ ${successMessage}`, 'success');
      
      await fetchDashboardData();
      setDefesaDialog(false);
      setAvaliacaoDialog(false);
      setDefesaData({
        dataDefesa: '',
        localDefesa: '',
        presidenteId: '',
        arguenteId: ''
      });
      setAvaliacaoData({
        notaTrabalhoEscrito: '',
        notaDefesa: '',
        observacoes: ''
      });
    } catch (error) {
      console.error(`Erro ao ${actionType} defesa:`, error);
      handleApiError(error, `${actionType === 'agendar' ? 'agendamento' : 'avaliação'} da defesa`);
    } finally {
      setActionLoading(false);
    }
  };

  // RENDERIZAÇÃO DOS CARTÕES DE RESUMO - MOBILE OPTIMIZED
  const renderSummaryCards = () => {
    const projetosArray = dashboardData?.projetos || [];
    const defesasArray = dashboardData?.defesas || [];
    const avaliacoesArray = dashboardData?.avaliacoes || [];
    const monografiasArray = dashboardData?.monografias || [];

    const summaryData = [
      {
        title: 'Projetos em Supervisão',
        value: projetosArray.filter(p => p.status === 'EM_ANDAMENTO').length,
        icon: <AssignmentIcon />,
        color: '#1976d2'
      },
      {
        title: 'Monografias',
        value: monografiasArray.length,
        icon: <DescriptionIcon />,
        color: '#9c27b0'
      },
      {
        title: 'Defesas Pendentes',
        value: defesasArray.filter(d => d.status === 'PENDENTE').length,
        icon: <SchoolIcon />,
        color: '#2e7d32'
      },
      {
        title: 'Avaliações a Realizar',
        value: avaliacoesArray.filter(a => a.status === 'PENDENTE').length,
        icon: <AssessmentIcon />,
        color: '#ed6c02'
      }
    ];

    return (
      <Grid container spacing={isMobile ? 2 : 3} sx={{ mb: 4 }}>
        {summaryData.map((item, index) => (
          <Grid item xs={6} sm={6} md={3} key={index}>
            <Card
              sx={{
                height: '100%',
                cursor: 'pointer',
                minHeight: isMobile ? '100px' : '120px',
                '&:hover': {
                  boxShadow: 6,
                  transform: 'translateY(-2px)',
                  transition: 'all 0.3s'
                }
              }}
              onClick={() => setActiveTab(index)}
            >
              <CardContent sx={{ 
                p: isMobile ? 1.5 : 2,
                '&:last-child': { pb: isMobile ? 1.5 : 2 }
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: isMobile ? 'center' : 'flex-start', 
                  mb: isMobile ? 1 : 2,
                  textAlign: isMobile ? 'center' : 'left'
                }}>
                  <Box
                    sx={{
                      p: isMobile ? 0.5 : 1,
                      borderRadius: 1,
                      bgcolor: `${item.color}20`,
                      color: item.color,
                      mr: isMobile ? 0 : 2,
                      mb: isMobile ? 1 : 0
                    }}
                  >
                    {React.cloneElement(item.icon, { fontSize: isMobile ? 'small' : 'medium' })}
                  </Box>
                  <Typography 
                    variant={isMobile ? "body2" : "h6"} 
                    color="text.secondary"
                    sx={{ 
                      fontSize: isMobile ? '0.75rem' : undefined,
                      lineHeight: isMobile ? 1.2 : undefined
                    }}
                  >
                    {item.title}
                  </Typography>
                </Box>
                <Typography 
                  variant={isMobile ? "h5" : "h4"} 
                  component="div"
                  sx={{ 
                    fontWeight: 'bold',
                    textAlign: isMobile ? 'center' : 'left'
                  }}
                >
                  {item.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  // RENDERIZAÇÃO DE PROPOSTAS - MOBILE OPTIMIZED
  const renderPropostas = () => {
    const propostas = dashboardData.propostas || [];
    
    if (propostas.length === 0) {
      return (
        <Alert severity="info" icon={<ArticleIcon />}>
          Não há propostas para supervisão no momento.
        </Alert>
      );
    }

    const propostasOrdenadas = sortItemsByDate(propostas, 'proposta');

    if (isMobile) {
      return (
        <Stack spacing={2}>
          {propostasOrdenadas.map((proposta) => {
            const isNovo = isNewItem(proposta, 'proposta');
            
            return (
              <MobileCard
                key={proposta.id}
                title={proposta.titulo || 'Título não disponível'}
                status={proposta.status}
                isNew={isNovo}
                actions={
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {(proposta.documento_proposta || proposta.arquivo_url) && (
                      <>
                        <Button
                          size="small"
                          variant="outlined"
                          color="info"
                          startIcon={<VisibilityIcon />}
                          onClick={() => handleViewDocument(proposta, 'proposta')}
                          sx={{ fontSize: '0.7rem', minWidth: 'auto', px: 1 }}
                        >
                          Ver
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="secondary"
                          startIcon={<DownloadIcon />}
                          onClick={() => handleDownloadDocument(proposta, 'proposta')}
                          sx={{ fontSize: '0.7rem', minWidth: 'auto', px: 1 }}
                        >
                          Download
                        </Button>
                      </>
                    )}
                    
                    <Button
                      size="small"
                      startIcon={<ChatIcon />}
                      onClick={() => openChatDialogHandler(proposta, 'proposta')}
                      sx={{ fontSize: '0.7rem', minWidth: 'auto', px: 1 }}
                    >
                      Chat
                    </Button>
                    
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<VisibilityIcon />}
                      onClick={() => navigate(`/propostas/${proposta.id}`)}
                      sx={{ fontSize: '0.7rem', minWidth: 'auto', px: 1 }}
                    >
                      Detalhes
                    </Button>
                  </Stack>
                }
              >
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Estudante:</strong> {proposta.estudante?.nome || 'N/A'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Área:</strong> {proposta.area_conhecimento || 'N/A'}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Submetida:</strong> {proposta.created_at ? format(new Date(proposta.created_at), 'dd/MM/yyyy HH:mm', { locale: ptBR }) : 'N/A'}
                  </Typography>

                  {proposta.resumo && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      <strong>Resumo:</strong> {proposta.resumo.substring(0, 100)}...
                    </Typography>
                  )}

                  {proposta.observacoes && (
                    <Alert severity="info" sx={{ mt: 1, fontSize: '0.75rem' }}>
                      <Typography variant="caption">
                        <strong>Observações:</strong> {proposta.observacoes}
                      </Typography>
                    </Alert>
                  )}
                </Box>
              </MobileCard>
            );
          })}
        </Stack>
      );
    }

    // Renderização desktop (original)
    return (
      <Grid container spacing={3}>
        {propostasOrdenadas.map((proposta) => {
          const isNovo = isNewItem(proposta, 'proposta');
          
          return (
            <Grid item xs={12} md={6} key={proposta.id}>
              <Card 
                sx={{ 
                  height: '100%',
                  border: isNovo ? '2px solid #2196f3' : '1px solid rgba(0,0,0,0.12)',
                  boxShadow: isNovo ? '0 4px 8px rgba(33, 150, 243, 0.3)' : undefined
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="h6" gutterBottom>
                          {proposta.titulo || 'Título não disponível'}
                        </Typography>
                        {isNovo && (
                          <Chip
                            label="NOVO"
                            size="small"
                            color="primary"
                            sx={{ 
                              fontWeight: 'bold',
                              animation: 'pulse 2s infinite',
                              '@keyframes pulse': {
                                '0%': { opacity: 1 },
                                '50%': { opacity: 0.7 },
                                '100%': { opacity: 1 }
                              }
                            }}
                          />
                        )}
                      </Box>
                      <Chip
                        label={proposta.status}
                        color={
                          proposta.status === 'APROVADA' ? 'success' :
                          proposta.status === 'SUBMETIDA' ? 'info' :
                          proposta.status === 'EM_REVISAO' ? 'warning' :
                          proposta.status === 'PENDENTE' ? 'default' :
                          proposta.status === 'REJEITADA' ? 'error' : 'default'
                        }
                        size="small"
                        sx={{ mb: 2 }}
                      />
                    </Box>
                  </Box>

                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Estudante: {proposta.estudante?.nome || 'N/A'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Área: {proposta.area_conhecimento || 'N/A'}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Submetida em: {proposta.created_at ? format(new Date(proposta.created_at), 'dd/MM/yyyy HH:mm', { locale: ptBR }) : 'N/A'}
                  </Typography>

                  {proposta.resumo && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
                      <strong>Resumo:</strong> {proposta.resumo.substring(0, 100)}...
                    </Typography>
                  )}

                  {proposta.observacoes && (
                    <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                      <Typography variant="caption">
                        <strong>Observações:</strong> {proposta.observacoes}
                      </Typography>
                    </Alert>
                  )}
                </CardContent>

                <CardActions sx={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, p: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {(proposta.documento_proposta || proposta.arquivo_url) && (
                      <>
                        <Button
                          size="small"
                          variant="outlined"
                          color="info"
                          startIcon={<VisibilityIcon />}
                          onClick={() => handleViewDocument(proposta, 'proposta')}
                        >
                          Visualizar
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="secondary"
                          startIcon={<DownloadIcon />}
                          onClick={() => handleDownloadDocument(proposta, 'proposta')}
                        >
                          Download
                        </Button>
                      </>
                    )}
                    
                    <Button
                      size="small"
                      startIcon={<ChatIcon />}
                      onClick={() => openChatDialogHandler(proposta, 'proposta')}
                    >
                      Chat
                    </Button>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<VisibilityIcon />}
                      onClick={() => navigate(`/propostas/${proposta.id}`)}
                    >
                      Ver Detalhes
                    </Button>
                  </Box>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  // RENDERIZAÇÃO DE PROJETOS - MOBILE OPTIMIZED
  const renderProjetos = () => {
    const projetos = dashboardData.projetos || [];
    
    if (projetos.length === 0) {
      return (
        <Alert severity="info" icon={<AssignmentIcon />}>
          Não há projetos para supervisão no momento.
        </Alert>
      );
    }

    const projetosOrdenados = sortItemsByDate(projetos, 'projeto');

    if (isMobile) {
      return (
        <Stack spacing={2}>
          {projetosOrdenados.map((projeto) => {
            const isNovo = isNewItem(projeto, 'projeto');
            
            return (
              <MobileCard
                key={projeto.id}
                title={projeto.proposta?.titulo || 'Título não disponível'}
                status={projeto.status}
                isNew={isNovo}
                actions={
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {(projeto.documento_projeto || projeto.arquivo_url) && (
                      <>
                        <Button
                          size="small"
                          variant="outlined"
                          color="info"
                          startIcon={<VisibilityIcon />}
                          onClick={() => handleViewDocument(projeto, 'projeto')}
                          sx={{ fontSize: '0.7rem', minWidth: 'auto', px: 1 }}
                        >
                          Ver
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="secondary"
                          startIcon={<DownloadIcon />}
                          onClick={() => handleDownloadDocument(projeto, 'projeto')}
                          sx={{ fontSize: '0.7rem', minWidth: 'auto', px: 1 }}
                        >
                          Download
                        </Button>
                      </>
                    )}
                    
                    <Button
                      size="small"
                      startIcon={<ChatIcon />}
                      onClick={() => openChatDialogHandler(projeto, 'projeto')}
                      sx={{ fontSize: '0.7rem', minWidth: 'auto', px: 1 }}
                    >
                      Chat
                    </Button>
                    
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<VisibilityIcon />}
                      onClick={() => navigate(`/projetos/${projeto.id}`)}
                      sx={{ fontSize: '0.7rem', minWidth: 'auto', px: 1 }}
                    >
                      Detalhes
                    </Button>
                  </Stack>
                }
              >
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Estudante:</strong> {projeto.proposta?.estudante?.nome || 'N/A'}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Supervisor:</strong> {user?.nome || 'N/A'}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Iniciado:</strong> {projeto.created_at ? format(new Date(projeto.created_at), 'dd/MM/yyyy HH:mm', { locale: ptBR }) : 'N/A'}
                  </Typography>

                  {projeto.data_prevista_conclusao && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Previsão:</strong> {format(new Date(projeto.data_prevista_conclusao), 'dd/MM/yyyy', { locale: ptBR })}
                    </Typography>
                  )}

                  {projeto.observacoes && (
                    <Alert severity="info" sx={{ mt: 1, fontSize: '0.75rem' }}>
                      <Typography variant="caption">
                        <strong>Observações:</strong> {projeto.observacoes}
                      </Typography>
                    </Alert>
                  )}
                </Box>
              </MobileCard>
            );
          })}
        </Stack>
      );
    }

    // Renderização desktop (original)
    return (
      <Grid container spacing={3}>
        {projetosOrdenados.map((projeto) => {
          const isNovo = isNewItem(projeto, 'projeto');
          
          return (
            <Grid item xs={12} md={6} key={projeto.id}>
              <Card 
                sx={{ 
                  height: '100%',
                  border: isNovo ? '2px solid #ff9800' : '1px solid rgba(0,0,0,0.12)',
                  boxShadow: isNovo ? '0 4px 8px rgba(255, 152, 0, 0.3)' : undefined
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="h6" gutterBottom>
                          {projeto.proposta?.titulo || 'Título não disponível'}
                        </Typography>
                        {isNovo && (
                          <Chip
                            label="NOVO"
                            size="small"
                            color="warning"
                            sx={{ 
                              fontWeight: 'bold',
                              color: 'white',
                              animation: 'pulse 2s infinite',
                              '@keyframes pulse': {
                                '0%': { opacity: 1 },
                                '50%': { opacity: 0.7 },
                                '100%': { opacity: 1 }
                              }
                            }}
                          />
                        )}
                      </Box>
                      <Chip
                        label={projeto.status}
                        color={
                          projeto.status === 'APROVADO' ? 'success' :
                          projeto.status === 'EM_ANDAMENTO' ? 'info' :
                          projeto.status === 'EM_REVISAO' ? 'warning' :
                          projeto.status === 'CONCLUIDO' ? 'success' :
                          projeto.status === 'CANCELADO' ? 'error' : 'default'
                        }
                        size="small"
                        sx={{ mb: 2 }}
                      />
                    </Box>
                  </Box>

                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Estudante: {projeto.proposta?.estudante?.nome || 'N/A'}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Supervisor: {user?.nome || 'N/A'}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Iniciado em: {projeto.created_at ? format(new Date(projeto.created_at), 'dd/MM/yyyy HH:mm', { locale: ptBR }) : 'N/A'}
                  </Typography>

                  {projeto.data_prevista_conclusao && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Previsão de conclusão: {format(new Date(projeto.data_prevista_conclusao), 'dd/MM/yyyy', { locale: ptBR })}
                    </Typography>
                  )}

                  {projeto.observacoes && (
                    <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                      <Typography variant="caption">
                        <strong>Observações:</strong> {projeto.observacoes}
                      </Typography>
                    </Alert>
                  )}
                </CardContent>

                <CardActions sx={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, p: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {(projeto.documento_projeto || projeto.arquivo_url) && (
                      <>
                        <Button
                          size="small"
                          variant="outlined"
                          color="info"
                          startIcon={<VisibilityIcon />}
                          onClick={() => handleViewDocument(projeto, 'projeto')}
                        >
                          Visualizar
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="secondary"
                          startIcon={<DownloadIcon />}
                          onClick={() => handleDownloadDocument(projeto, 'projeto')}
                        >
                          Download
                        </Button>
                      </>
                    )}
                    
                    <Button
                      size="small"
                      startIcon={<ChatIcon />}
                      onClick={() => openChatDialogHandler(projeto, 'projeto')}
                    >
                      Chat
                    </Button>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<VisibilityIcon />}
                      onClick={() => navigate(`/projetos/${projeto.id}`)}
                    >
                      Ver Detalhes
                    </Button>
                  </Box>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    );
    };

  // RENDERIZAÇÃO DE MONOGRAFIAS - MOBILE OPTIMIZED
  const renderMonografias = () => {
    const monografias = dashboardData.monografias || [];
    
    const renderHeader = () => (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 2 : 0
      }}>
        <Typography variant={isMobile ? "h6" : "h5"} color="primary.main">
          Monografias Supervisionadas ({monografias.length})
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={refreshing}
          color="primary"
          size={isMobile ? "small" : "medium"}
        >
          {refreshing ? 'Atualizando...' : 'Atualizar Lista'}
        </Button>
      </Box>
    );

    if (monografias.length === 0) {
      return (
        <>
          {renderHeader()}
          <Alert severity="info" icon={<DescriptionIcon />}>
            Não há monografias para supervisão no momento.
          </Alert>
        </>
      );
    }

    const monografiasOrdenadas = sortItemsByDate(monografias, 'monografia');

    if (isMobile) {
      return (
        <>
          {renderHeader()}
          <Stack spacing={2}>
            {monografiasOrdenadas
              .filter(monografia => {
                const isValid = monografia && 
                               typeof monografia === 'object' && 
                               monografia.id;
                if (!isValid) {
                  console.warn("⚠️ Monografia inválida filtrada:", monografia);
                }
                return isValid;
              })
              .map((monografia) => {
                if (!monografia) return null;
                const isNovo = isNewItem(monografia, 'monografia');
                const statusColor = getStatusColor(monografia.status);
                const statusDisplayName = getStatusDisplayName(monografia.status);
                const availableActions = getAvailableActions('supervisor', monografia.status);
                
                return (
                  <MobileCard
                    key={monografia.id}
                    title={monografia?.projeto?.proposta?.titulo || monografia?.titulo || 'Título não disponível'}
                    status={statusDisplayName}
                    isNew={isNovo}
                    actions={
                      <Stack spacing={1}>
                        {/* Botões de documento */}
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {(monografia.documento_monografia || monografia.arquivo_url || monografia.documento_url) && (
                            <>
                              <Button
                                size="small"
                                variant="outlined"
                                color="info"
                                startIcon={<VisibilityIcon />}
                                onClick={() => handleViewDocument(monografia, 'monografia')}
                                sx={{ fontSize: '0.7rem', minWidth: 'auto', px: 1 }}
                              >
                                Ver
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                startIcon={<DownloadIcon />}
                                onClick={() => handleDownloadDocument(monografia, 'monografia')}
                                sx={{ fontSize: '0.7rem', minWidth: 'auto', px: 1 }}
                              >
                                Download
                              </Button>
                            </>
                          )}
                          
                          <Button
                            size="small"
                            startIcon={<ChatIcon />}
                            onClick={() => openChatDialogHandler(monografia, 'monografia')}
                            sx={{ fontSize: '0.7rem', minWidth: 'auto', px: 1 }}
                          >
                            Chat
                          </Button>
                        </Stack>
                        
                        {/* Botões de ação específicos */}
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {availableActions.map((action) => {
                            let IconComponent;
                            switch (action.icon) {
                              case 'PlayArrow': IconComponent = PlayArrowIcon; break;
                              case 'Edit': IconComponent = EditIcon; break;
                              case 'CheckCircle': IconComponent = CheckCircleIcon; break;
                              case 'Send': IconComponent = SendIcon; break;
                              default: IconComponent = InfoIcon;
                            }
                            
                            return (
                              <Button
                                key={action.key}
                                size="small"
                                color={action.color}
                                variant="contained"
                                startIcon={<IconComponent />}
                                onClick={() => handleMonografiaAction(monografia, action.key)}
                                sx={{ fontSize: '0.7rem', minWidth: 'auto', px: 1 }}
                              >
                                {action.label}
                              </Button>
                            );
                          })}
                          
                          <Button
                            variant="outlined"
                            size="small"
                            color="info"
                            startIcon={<EventIcon />}
                            onClick={() => handleAbrirHistorico(monografia)}
                            sx={{ fontSize: '0.7rem', minWidth: 'auto', px: 1 }}
                          >
                            Histórico
                          </Button>

                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<VisibilityIcon />}
                            onClick={() => navigate(`/monografias/${monografia.id}`)}
                            sx={{ fontSize: '0.7rem', minWidth: 'auto', px: 1 }}
                          >
                            Detalhes
                          </Button>
                        </Stack>
                      </Stack>
                    }
                  >
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Estudante:</strong> {monografia.projeto?.proposta?.estudante?.nome || 'N/A'}
                      </Typography>
                      
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Versão:</strong> {monografia.versao_documento || monografia.versaoDocumento || 'N/A'}
                      </Typography>

                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Versão atual:</strong> {monografia.versao_atual || 'v1.0'}
                      </Typography>

                      {monografia.numero_resubmissoes > 0 && (
                        <Typography variant="body2" color="warning.main" gutterBottom>
                          <strong>Resubmissões:</strong> {monografia.numero_resubmissoes}
                        </Typography>
                      )}
                      
                      {/* Datas específicas baseadas no status */}
                      {monografia.data_submissao && (
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          <strong>Submetida:</strong> {format(new Date(monografia.data_submissao), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                        </Typography>
                      )}
                      
                      {monografia.data_inicio_analise && (
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          <strong>Análise iniciada:</strong> {format(new Date(monografia.data_inicio_analise), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                        </Typography>
                      )}
                      
                      {monografia.data_resubmissao && (
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          <strong>Resubmetida:</strong> {format(new Date(monografia.data_resubmissao), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                        </Typography>
                      )}
                      
                      {monografia.data_aprovacao_supervisor && (
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          <strong>Aprovada:</strong> {format(new Date(monografia.data_aprovacao_supervisor), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                        </Typography>
                      )}

                      {/* Prazo de correções */}
                      {monografia.prazo_correcoes && monografia.status === 'CORRECOES_SOLICITADAS' && (
                        <Alert 
                          severity={new Date(monografia.prazo_correcoes) < new Date() ? 'error' : 'warning'} 
                          sx={{ mt: 1, fontSize: '0.75rem' }}
                        >
                          <Typography variant="caption">
                            <strong>Prazo para correções:</strong> {format(new Date(monografia.prazo_correcoes), 'dd/MM/yyyy', { locale: ptBR })}
                            {new Date(monografia.prazo_correcoes) < new Date() && ' (VENCIDO)'}
                          </Typography>
                        </Alert>
                      )}

                      {/* Observações específicas */}
                      {monografia.observacoes_supervisor && (
                        <Alert severity="info" sx={{ mt: 1, fontSize: '0.75rem' }}>
                          <Typography variant="caption">
                            <strong>Observações do Supervisor:</strong> {monografia.observacoes_supervisor}
                          </Typography>
                        </Alert>
                      )}
                      
                      {monografia.observacoes_aprovacao_supervisor && (
                        <Alert severity="success" sx={{ mt: 1, fontSize: '0.75rem' }}>
                          <Typography variant="caption">
                            <strong>Observações da Aprovação:</strong> {monografia.observacoes_aprovacao_supervisor}
                          </Typography>
                        </Alert>
                      )}
                      
                      {monografia.observacoes_devolucao_direcao && (
                        <Alert severity="error" sx={{ mt: 1, fontSize: '0.75rem' }}>
                          <Typography variant="caption">
                            <strong>Devolvida pela Direção ({monografia.tipo_devolucao_direcao}):</strong> {monografia.observacoes_devolucao_direcao}
                          </Typography>
                        </Alert>
                      )}
                    </Box>
                  </MobileCard>
                );
              })}
          </Stack>
        </>
      );
    }

    // Renderização desktop (original)
    return (
      <>
        {renderHeader()}
        <Grid container spacing={3}>
          {monografiasOrdenadas
            .filter(monografia => {
              const isValid = monografia && 
                             typeof monografia === 'object' && 
                             monografia.id;
              if (!isValid) {
                console.warn("⚠️ Monografia inválida filtrada:", monografia);
              }
              return isValid;
            })
            .map((monografia) => {
              if (!monografia) return null;
              const isNovo = isNewItem(monografia, 'monografia');
              const statusColor = getStatusColor(monografia.status);
              const statusDisplayName = getStatusDisplayName(monografia.status);
              const availableActions = getAvailableActions('supervisor', monografia.status);
              
              return (
                <Grid item xs={12} md={6} key={monografia.id}>
                  <Card 
                    sx={{ 
                      height: '100%',
                      border: isNovo ? '2px solid #4caf50' : '1px solid rgba(0,0,0,0.12)',
                      boxShadow: isNovo ? '0 4px 8px rgba(76, 175, 80, 0.3)' : undefined
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography variant="h6" gutterBottom>
                              {monografia?.projeto?.proposta?.titulo || monografia?.titulo || 'Título não disponível'}
                            </Typography>
                            {isNovo && (
                              <Chip
                                label="NOVO"
                                size="small"
                                color="success"
                                sx={{ 
                                  fontWeight: 'bold',
                                  animation: 'pulse 2s infinite',
                                  '@keyframes pulse': {
                                    '0%': { opacity: 1 },
                                    '50%': { opacity: 0.7 },
                                    '100%': { opacity: 1 }
                                  }
                                }}
                              />
                            )}
                          </Box>
                          
                          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                            <Chip
                              label={statusDisplayName}
                              color={statusColor}
                              size="small"
                              sx={{ fontWeight: 'bold' }}
                            />
                            
                            {monografia.numero_resubmissoes > 0 && (
                              <Chip
                                label={`${monografia.numero_resubmissoes} resubmissão${monografia.numero_resubmissoes > 1 ? 'ões' : ''}`}
                                size="small"
                                variant="outlined"
                                color="info"
                              />
                            )}
                          </Box>
                        </Box>
                      </Box>

                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Estudante: {monografia.projeto?.proposta?.estudante?.nome || 'N/A'}
                      </Typography>
                      
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Versão: {monografia.versao_documento || monografia.versaoDocumento || 'N/A'}
                      </Typography>

                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Versão atual: {monografia.versao_atual || 'v1.0'}
                      </Typography>

                      {monografia.numero_resubmissoes > 0 && (
                        <Typography variant="body2" color="warning.main" gutterBottom>
                          {monografia.numero_resubmissoes} resubmissão{monografia.numero_resubmissoes > 1 ? 'ões' : ''}
                        </Typography>
                      )}
                      
                      {/* Datas específicas baseadas no status */}
                      {monografia.data_submissao && (
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Submetida em: {format(new Date(monografia.data_submissao), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                        </Typography>
                      )}
                      
                      {monografia.data_inicio_analise && (
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Análise iniciada: {format(new Date(monografia.data_inicio_analise), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                        </Typography>
                      )}
                      
                      {monografia.data_resubmissao && (
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Resubmetida em: {format(new Date(monografia.data_resubmissao), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                        </Typography>
                      )}
                      
                      {monografia.data_aprovacao_supervisor && (
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Aprovada em: {format(new Date(monografia.data_aprovacao_supervisor), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                        </Typography>
                      )}

                      {/* Prazo de correções */}
                      {monografia.prazo_correcoes && monografia.status === 'CORRECOES_SOLICITADAS' && (
                        <Alert 
                          severity={new Date(monografia.prazo_correcoes) < new Date() ? 'error' : 'warning'} 
                          sx={{ mt: 2, mb: 2 }}
                        >
                          <Typography variant="caption">
                            <strong>Prazo para correções:</strong> {format(new Date(monografia.prazo_correcoes), 'dd/MM/yyyy', { locale: ptBR })}
                            {new Date(monografia.prazo_correcoes) < new Date() && ' (VENCIDO)'}
                          </Typography>
                        </Alert>
                      )}

                      {/* Observações específicas */}
                      {monografia.observacoes_supervisor && (
                        <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                          <Typography variant="caption">
                            <strong>Observações do Supervisor:</strong> {monografia.observacoes_supervisor}
                          </Typography>
                        </Alert>
                      )}
                      
                      {monografia.observacoes_aprovacao_supervisor && (
                        <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
                          <Typography variant="caption">
                            <strong>Observações da Aprovação:</strong> {monografia.observacoes_aprovacao_supervisor}
                          </Typography>
                        </Alert>
                      )}
                      
                      {monografia.observacoes_devolucao_direcao && (
                        <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                          <Typography variant="caption">
                            <strong>Devolvida pela Direção ({monografia.tipo_devolucao_direcao}):</strong> {monografia.observacoes_devolucao_direcao}
                          </Typography>
                        </Alert>
                      )}
                    </CardContent>

                    <CardActions sx={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, p: 2 }}>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {/* Botões de documento */}
                        {(monografia.documento_monografia || monografia.arquivo_url || monografia.documento_url) && (
                          <>
                            <Button
                              size="small"
                              variant="outlined"
                              color="info"
                              startIcon={<VisibilityIcon />}
                              onClick={() => handleViewDocument(monografia, 'monografia')}
                            >
                              Visualizar
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="secondary"
                              startIcon={<DownloadIcon />}
                              onClick={() => handleDownloadDocument(monografia, 'monografia')}
                            >
                              Download
                            </Button>
                          </>
                        )}
                        
                        <Button
                          size="small"
                          startIcon={<ChatIcon />}
                          onClick={() => openChatDialogHandler(monografia, 'monografia')}
                        >
                          Chat
                        </Button>
                      </Box>

                      {/* Botões de ação específicos baseados no status */}
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {availableActions.map((action) => {
                          let IconComponent;
                          switch (action.icon) {
                            case 'PlayArrow': IconComponent = PlayArrowIcon; break;
                            case 'Edit': IconComponent = EditIcon; break;
                            case 'CheckCircle': IconComponent = CheckCircleIcon; break;
                            case 'Send': IconComponent = SendIcon; break;
                            default: IconComponent = InfoIcon;
                          }
                          
                          return (
                            <Button
                              key={action.key}
                              size="small"
                              color={action.color}
                              variant="contained"
                              startIcon={<IconComponent />}
                              onClick={() => handleMonografiaAction(monografia, action.key)}
                            >
                              {action.label}
                            </Button>
                          );
                        })}
                        
                        {/* BOTÕES DE HISTÓRICO */}
                        <Button
                          variant="outlined"
                          size="small"
                          color="info"
                          startIcon={<EventIcon />}
                          onClick={() => handleAbrirHistorico(monografia)}
                        >
                          Histórico
                        </Button>

                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<VisibilityIcon />}
                          onClick={() => navigate(`/monografias/${monografia.id}`)}
                        >
                          Detalhes
                        </Button>
                      </Box>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
        </Grid>
      </>
    );
  };

  // RENDERIZAÇÃO DE DEFESAS - MOBILE OPTIMIZED
  const renderDefesas = () => {
    const defesas = dashboardData.defesas || [];
    
    if (defesas.length === 0) {
      return (
        <Alert severity="info" icon={<SchoolIcon />}>
          Não há defesas para supervisão no momento.
        </Alert>
      );
    }

    const defesasOrdenadas = sortItemsByDate(defesas, 'defesa');

    if (isMobile) {
      return (
        <Stack spacing={2}>
          {defesasOrdenadas.map((defesa) => {
            const isNovo = isNewItem(defesa, 'defesa');
            
            const actions = (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  size="small"
                  startIcon={<VisibilityIcon />}
                  onClick={() => navigate(`/defesas/${defesa.id}`)}
                  sx={{ fontSize: '0.7rem', minWidth: 'auto', px: 1 }}
                >
                  Detalhes
                </Button>
                
                <Button
                  size="small"
                  startIcon={<ChatIcon />}
                  onClick={() => openChatDialogHandler(defesa, 'defesa')}
                  sx={{ fontSize: '0.7rem', minWidth: 'auto', px: 1 }}
                >
                  Chat
                </Button>

                {defesa.status === 'PENDENTE' && (
                  <Button
                    size="small"
                    color="primary"
                    variant="contained"
                    startIcon={<EventIcon />}
                    onClick={() => handleDefesaAction(defesa, 'agendar')}
                    sx={{ fontSize: '0.7rem', minWidth: 'auto', px: 1 }}
                  >
                    Agendar
                  </Button>
                )}

                {defesa.status === 'REALIZADA' && !defesa.avaliacao && (
                  <Button
                    size="small"
                    color="success"
                    variant="contained"
                    startIcon={<GradeIcon />}
                    onClick={() => handleDefesaAction(defesa, 'avaliar')}
                    sx={{ fontSize: '0.7rem', minWidth: 'auto', px: 1 }}
                  >
                    Avaliar
                  </Button>
                )}
              </Stack>
            );
            
            return (
              <MobileCard
                key={defesa.id}
                title={defesa.monografia?.projeto?.proposta?.titulo || 'Título não disponível'}
                status={defesa.status}
                isNew={isNovo}
                actions={actions}
              >
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Estudante:</strong> {defesa.monografia?.projeto?.proposta?.estudante?.nome || 'N/A'}
                  </Typography>

                  {defesa.data_defesa && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Data:</strong> {format(new Date(defesa.data_defesa), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                    </Typography>
                  )}

                  {defesa.local_defesa && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Local:</strong> {defesa.local_defesa}
                    </Typography>
                  )}

                  {defesa.presidente && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Presidente:</strong> {defesa.presidente.nome}
                    </Typography>
                  )}

                  {defesa.arguente && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Arguente:</strong> {defesa.arguente.nome}
                    </Typography>
                  )}

                  {defesa.observacoes && (
                    <Alert severity="info" sx={{ mt: 1, fontSize: '0.75rem' }}>
                      <Typography variant="caption">
                        <strong>Observações:</strong> {defesa.observacoes}
                      </Typography>
                    </Alert>
                  )}
                </Box>
              </MobileCard>
            );
          })}
        </Stack>
      );
    }

    // Renderização desktop (original)
    return (
      <Grid container spacing={3}>
        {defesasOrdenadas.map((defesa) => {
          const isNovo = isNewItem(defesa, 'defesa');
          
          return (
            <Grid item xs={12} md={6} key={defesa.id}>
              <Card 
                sx={{ 
                  height: '100%',
                  border: isNovo ? '2px solid #9c27b0' : '1px solid rgba(0,0,0,0.12)',
                  boxShadow: isNovo ? '0 4px 8px rgba(156, 39, 176, 0.3)' : undefined
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="h6" gutterBottom>
                          {defesa.monografia?.projeto?.proposta?.titulo || 'Título não disponível'}
                        </Typography>
                        {isNovo && (
                          <Chip
                            label="NOVO"
                            size="small"
                            color="secondary"
                            sx={{ 
                              fontWeight: 'bold',
                              animation: 'pulse 2s infinite',
                              '@keyframes pulse': {
                                '0%': { opacity: 1 },
                                '50%': { opacity: 0.7 },
                                '100%': { opacity: 1 }
                              }
                            }}
                          />
                        )}
                      </Box>
                      <Chip
                        label={defesa.status}
                        color={
                          defesa.status === 'REALIZADA' ? 'success' :
                          defesa.status === 'AGENDADA' ? 'info' :
                          defesa.status === 'PENDENTE' ? 'warning' :
                          defesa.status === 'CANCELADA' ? 'error' : 'default'
                        }
                        size="small"
                        sx={{ mb: 2 }}
                      />
                    </Box>
                  </Box>

                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Estudante: {defesa.monografia?.projeto?.proposta?.estudante?.nome || 'N/A'}
                  </Typography>

                  {defesa.data_defesa && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Data: {format(new Date(defesa.data_defesa), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                    </Typography>
                  )}

                  {defesa.local_defesa && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Local: {defesa.local_defesa}
                    </Typography>
                  )}

                  {defesa.presidente && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Presidente: {defesa.presidente.nome}
                    </Typography>
                  )}

                  {defesa.arguente && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Arguente: {defesa.arguente.nome}
                    </Typography>
                  )}

                  {defesa.observacoes && (
                    <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                      <Typography variant="caption">
                        <strong>Observações:</strong> {defesa.observacoes}
                      </Typography>
                    </Alert>
                  )}
                </CardContent>

                <CardActions sx={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      size="small"
                      startIcon={<VisibilityIcon />}
                      onClick={() => navigate(`/defesas/${defesa.id}`)}
                    >
                      Ver Detalhes
                    </Button>
                    
                    <Button
                      size="small"
                      startIcon={<ChatIcon />}
                      onClick={() => openChatDialogHandler(defesa, 'defesa')}
                    >
                      Chat
                    </Button>
                  </Box>

                  {defesa.status === 'PENDENTE' && (
                    <Button
                      size="small"
                      color="primary"
                      variant="contained"
                      startIcon={<EventIcon />}
                      onClick={() => handleDefesaAction(defesa, 'agendar')}
                      >
                      Agendar
                    </Button>
                  )}

                  {defesa.status === 'REALIZADA' && !defesa.avaliacao && (
                    <Button
                      size="small"
                      color="success"
                      variant="contained"
                      startIcon={<GradeIcon />}
                      onClick={() => handleDefesaAction(defesa, 'avaliar')}
                    >
                      Avaliar
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  // RENDERIZAÇÃO DAS AVALIAÇÕES - MOBILE OPTIMIZED
  const renderAvaliacoes = () => (
    <Stack spacing={3}>
      <Typography variant={isMobile ? "h6" : "h5"} gutterBottom>
        Avaliações Pendentes
      </Typography>
      <AvaliacoesPendentes userRole="supervisor" />
      <Divider sx={{ my: 4 }} />
      <Typography variant={isMobile ? "h6" : "h5"} gutterBottom>
        Todas as Avaliações
      </Typography>
      <AvaliacaoList
        data={dashboardData.avaliacoes}
        onUpdate={fetchDashboardData}
      />
    </Stack>
  );

  // MOBILE TABS COMPONENT
  const MobileTabs = () => (
    <Paper 
      sx={{ 
        position: 'sticky',
        top: 0,
        zIndex: 1100,
        mb: 3
      }}
    >
      <Tabs
        value={activeTab}
        onChange={(_, newValue) => setActiveTab(newValue)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          '& .MuiTab-root': {
            minWidth: 'auto',
            px: 1,
            fontSize: '0.8rem'
          }
        }}
      >
        <Tab 
          label="Propostas" 
          icon={<ArticleIcon fontSize="small" />} 
          iconPosition="top"
        />
        <Tab 
          label="Projetos" 
          icon={<AssignmentIcon fontSize="small" />} 
          iconPosition="top"
        />
        <Tab 
          label="Monografias" 
          icon={<DescriptionIcon fontSize="small" />} 
          iconPosition="top"
        />
        <Tab 
          label="Defesas" 
          icon={<SchoolIcon fontSize="small" />} 
          iconPosition="top"
        />
        <Tab 
          label="Avaliações" 
          icon={<AssessmentIcon fontSize="small" />} 
          iconPosition="top"
        />
      </Tabs>
    </Paper>
  );

  // MOBILE SPEED DIAL
  const MobileSpeedDial = () => (
    <SpeedDial
      ariaLabel="Ações rápidas"
      sx={{ 
        position: 'fixed', 
        bottom: 16, 
        right: 16,
        zIndex: 1200
      }}
      icon={<SpeedDialIcon />}
      open={speedDialOpen}
      onClose={() => setSpeedDialOpen(false)}
      onOpen={() => setSpeedDialOpen(true)}
    >
      <SpeedDialAction
        icon={<RefreshIcon />}
        tooltipTitle="Atualizar"
        onClick={() => {
          setSpeedDialOpen(false);
          handleRefresh();
        }}
      />
      <SpeedDialAction
        icon={<FilterListIcon />}
        tooltipTitle="Filtros"
        onClick={() => {
          setSpeedDialOpen(false);
          // Implementar filtros se necessário
        }}
      />
      <SpeedDialAction
        icon={<SortIcon />}
        tooltipTitle="Ordenar"
        onClick={() => {
          setSpeedDialOpen(false);
          // Implementar ordenação se necessário
        }}
      />
    </SpeedDial>
  );

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '400px' 
        }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={50} />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Carregando painel do supervisor...
            </Typography>
          </Box>
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Mobile Header */}
      {isMobile && (
        <AppBar position="sticky" color="default" elevation={1}>
          <Toolbar sx={{ justifyContent: 'space-between', minHeight: '56px !important' }}>
            <Typography variant="h6" component="div" sx={{ fontSize: '1rem' }}>
              Painel Supervisor
            </Typography>
            <IconButton
              edge="end"
              onClick={handleRefresh}
              disabled={refreshing}
              size="small"
            >
              <RefreshIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
      )}

      <Container 
        maxWidth={isMobile ? false : "lg"} 
        sx={{ 
          px: isMobile ? 1 : 3,
          py: isMobile ? 2 : 3 
        }}
      >
        {/* Desktop Header */}
        {!isMobile && (
          <DashboardHeader 
            onRefresh={handleRefresh}
            refreshing={refreshing}
          />
        )}

        {/* Indicador de carregamento global */}
        {refreshing && <LinearProgress sx={{ mb: 2 }} />}

        {/* Alert de erro */}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            action={
              <Button color="inherit" size="small" onClick={handleRefresh}>
                Tentar Novamente
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {/* Cards de resumo */}
        {renderSummaryCards()}

        {/* Tabs - Mobile vs Desktop */}
        {isMobile ? (
          <MobileTabs />
        ) : (
          <Paper sx={{ mb: 4 }}>
            <Tabs
              value={activeTab}
              onChange={(_, newValue) => setActiveTab(newValue)}
              sx={{ borderBottom: 1, borderColor: 'divider' }}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="Propostas" icon={<ArticleIcon />} iconPosition="start" />
              <Tab label="Projetos" icon={<AssignmentIcon />} iconPosition="start" />
              <Tab label="Monografias" icon={<DescriptionIcon />} iconPosition="start" />
              <Tab label="Defesas" icon={<SchoolIcon />} iconPosition="start" />
              <Tab label="Avaliações" icon={<AssessmentIcon />} iconPosition="start" />
            </Tabs>

            <Box sx={{ p: isMobile ? 2 : 3 }}>
              {activeTab === 0 && renderPropostas()}
              {activeTab === 1 && renderProjetos()}
              {activeTab === 2 && renderMonografias()}
              {activeTab === 3 && (
                <DefesaList
                  data={dashboardData.defesas}
                  onUpdate={fetchDashboardData}
                />
              )}
              {activeTab === 4 && renderAvaliacoes()}
            </Box>
          </Paper>
        )}

        {/* Mobile Tab Content */}
        {isMobile && (
          <Box sx={{ pb: 10 }}>
            {activeTab === 0 && renderPropostas()}
            {activeTab === 1 && renderProjetos()}
            {activeTab === 2 && renderMonografias()}
            {activeTab === 3 && (
              <DefesaList
                data={dashboardData.defesas}
                onUpdate={fetchDashboardData}
              />
            )}
            {activeTab === 4 && renderAvaliacoes()}
          </Box>
        )}

        {/* Mobile Speed Dial */}
        {isMobile && <MobileSpeedDial />}

        {/* DIÁLOGOS PARA AÇÕES DE MONOGRAFIAS */}
        
        {/* DIÁLOGO MARCAR COMO EM ANÁLISE */}
        <Dialog
          open={analiseDialogOpen}
          onClose={() => setAnaliseDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          fullScreen={isSmallMobile}
        >
          <DialogTitle>
            Marcar como Em Análise
          </DialogTitle>
          <DialogContent>
            <Typography gutterBottom>
              Deseja marcar esta monografia como &quot;Em Análise&quot;? Isso iniciará o processo
            </Typography>
            {selectedMonografia && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2">
                  {selectedMonografia.projeto?.proposta?.titulo || selectedMonografia.titulo || 'Título não disponível'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Estudante: {selectedMonografia.projeto?.proposta?.estudante?.nome || 'N/A'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Status atual: {getStatusDisplayName(selectedMonografia.status)}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 1 : 0 }}>
            <Button 
              onClick={() => setAnaliseDialogOpen(false)}
              fullWidth={isMobile}
            >
              Cancelar
            </Button>
            <Button
              onClick={executeMonografiaAction}
              color="primary"
              variant="contained"
              disabled={actionLoading}
              startIcon={actionLoading ? <CircularProgress size={16} /> : <PlayArrowIcon />}
              fullWidth={isMobile}
            >
              {actionLoading ? 'Processando...' : 'Marcar como Em Análise'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* DIÁLOGO SOLICITAR CORREÇÕES */}
        <Dialog
          open={correcoesDialogOpen}
          onClose={() => setCorrecoesDialogOpen(false)}
          maxWidth="md"
          fullWidth
          fullScreen={isSmallMobile}
        >
          <DialogTitle>
            Solicitar Correções
          </DialogTitle>
          <DialogContent>
            {selectedMonografia && (
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2">
                  {selectedMonografia.projeto?.proposta?.titulo || selectedMonografia.titulo || 'Título não disponível'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Estudante: {selectedMonografia.projeto?.proposta?.estudante?.nome || 'N/A'}
                </Typography>
              </Box>
            )}
            
            <TextField
              autoFocus
              margin="dense"
              label="Observações sobre as correções necessárias"
              fullWidth
              multiline
              rows={isMobile ? 3 : 4}
              variant="outlined"
              value={dadosCorrecoes.observacoes}
              onChange={(e) => setDadosCorrecoes({...dadosCorrecoes, observacoes: e.target.value})}
              placeholder="Descreva detalhadamente as correções que o estudante deve fazer..."
              helperText="Mínimo 10 caracteres. Seja específico sobre as mudanças necessárias."
              error={dadosCorrecoes.observacoes.length > 0 && dadosCorrecoes.observacoes.length < 10}
            />
            
            <TextField
              margin="dense"
              label="Prazo para correções (opcional)"
              type="date"
              fullWidth
              value={dadosCorrecoes.prazoCorrecao}
              onChange={(e) => setDadosCorrecoes({...dadosCorrecoes, prazoCorrecao: e.target.value})}
              InputLabelProps={{ shrink: true }}
              helperText="Se não especificado, será dado prazo padrão de 15 dias"
              sx={{ mt: 2 }}
            />

            {/* SEÇÃO DE UPLOAD DE ARQUIVO */}
            <Box sx={{ mt: 3, p: 2, border: '2px dashed', borderColor: 'primary.main', borderRadius: 2, bgcolor: 'primary.50' }}>
              <Typography variant={isMobile ? "body2" : "h6"} gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                Anexar Documento Corrigido (Opcional)
              </Typography>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Você pode anexar uma versão corrigida da monografia com suas anotações e sugestões.
                Formatos aceitos: PDF, Word (.doc/.docx) • Tamanho máximo: 10MB
              </Typography>

              {!previewArquivo ? (
                <Box sx={{ textAlign: 'center' }}>
                  <input
                    id="arquivo-correcao-input"
                    type="file"
                    accept=".pdf,.doc,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword"
                    onChange={handleArquivoCorrecaoChange}
                    style={{ display: 'none' }}
                  />
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<DescriptionIcon />}
                    onClick={() => document.getElementById('arquivo-correcao-input').click()}
                    size={isMobile ? "small" : "medium"}
                    fullWidth={isMobile}
                  >
                    Selecionar Arquivo
                  </Button>
                </Box>
              ) : (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  p: 2, 
                  bgcolor: 'success.50', 
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'success.main',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? 1 : 0
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon color="success" />
                    <Typography 
                      variant="body2" 
                      color="success.main" 
                      sx={{ 
                        fontWeight: 'medium',
                        wordBreak: 'break-word'
                      }}
                    >
                      {previewArquivo}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    color="error"
                    onClick={removerArquivoCorrecao}
                    startIcon={<DeleteIcon />}
                  >
                    Remover
                  </Button>
                </Box>
              )}

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="caption">
                  Dica: Use ferramentas como &quot;Controlar Alterações&quot; no Word ou anotações em PDF 
                  para marcar suas correções de forma clara para o estudante.
                </Typography>
              </Alert>
            </Box>
          </DialogContent>
          <DialogActions sx={{ flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 1 : 0 }}>
            <Button 
              onClick={() => setCorrecoesDialogOpen(false)}
              fullWidth={isMobile}
            >
              Cancelar
            </Button>
            <Button
              onClick={executeMonografiaAction}
              color="warning"
              variant="contained"
              disabled={actionLoading || dadosCorrecoes.observacoes.length < 10}
              startIcon={actionLoading ? <CircularProgress size={16} /> : <EditIcon />}
              fullWidth={isMobile}
            >
              {actionLoading ? 'Enviando...' : 'Solicitar Correções'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* DIÁLOGO APROVAR MONOGRAFIA */}
        <Dialog
          open={aprovacaoDialogOpen}
          onClose={() => setAprovacaoDialogOpen(false)}
          maxWidth="md"
          fullWidth
          fullScreen={isSmallMobile}
        >
          <DialogTitle>
            Aprovar Monografia
          </DialogTitle>
          <DialogContent>
            {selectedMonografia && (
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2">
                  {selectedMonografia.projeto?.proposta?.titulo || selectedMonografia.titulo || 'Título não disponível'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Estudante: {selectedMonografia.projeto?.proposta?.estudante?.nome || 'N/A'}
                </Typography>
              </Box>
            )}
            
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Ao aprovar esta monografia, o estudante poderá submetê-la à Direção Científica para aprovação final.
              </Typography>
            </Alert>
            
            <TextField
              margin="dense"
              label="Observações da aprovação (opcional)"
              fullWidth
              multiline
              rows={isMobile ? 3 : 4}
              variant="outlined"
              value={observacoesAprovacao}
              onChange={(e) => setObservacoesAprovacao(e.target.value)}
              placeholder="Comentários positivos sobre a qualidade do trabalho..."
            />
          </DialogContent>
          <DialogActions sx={{ flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 1 : 0 }}>
            <Button 
              onClick={() => setAprovacaoDialogOpen(false)}
              fullWidth={isMobile}
            >
              Cancelar
            </Button>
            <Button
              onClick={executeMonografiaAction}
              color="success"
              variant="contained"
              disabled={actionLoading}
              startIcon={actionLoading ? <CircularProgress size={16} /> : <CheckCircleIcon />}
              fullWidth={isMobile}
            >
              {actionLoading ? 'Aprovando...' : 'Aprovar Monografia'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* DIÁLOGO CONFIRMAÇÃO AÇÕES GENÉRICAS */}
        <Dialog
          open={actionDialogOpen}
          onClose={() => setActionDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          fullScreen={isSmallMobile}
        >
          <DialogTitle>
            {actionType === 'aprovar' ? 'Confirmar Aprovação' : 'Confirmar Devolução'}
          </DialogTitle>
          <DialogContent>
            <Typography>
              {actionType === 'aprovar' 
                ? 'Deseja aprovar esta monografia? Esta ação não pode ser desfeita.'
                : 'Deseja devolver esta monografia para correções? O estudante será notificado.'
              }
            </Typography>
            {selectedMonografia && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2">
                  {selectedMonografia.projeto?.proposta?.titulo || 'Título não disponível'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Estudante: {selectedMonografia.projeto?.proposta?.estudante?.nome || 'N/A'}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 1 : 0 }}>
            <Button 
              onClick={() => setActionDialogOpen(false)}
              fullWidth={isMobile}
            >
              Cancelar
            </Button>
            <Button
              onClick={executeMonografiaAction}
              color={actionType === 'aprovar' ? 'success' : 'error'}
              variant="contained"
              disabled={actionLoading}
              startIcon={actionLoading ? <CircularProgress size={16} /> : (actionType === 'aprovar' ? <ApproveIcon /> : <RejectIcon />)}
              fullWidth={isMobile}
            >
              {actionLoading ? 'Processando...' : (actionType === 'aprovar' ? 'Aprovar' : 'Devolver')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* DIÁLOGO OBSERVAÇÕES GENÉRICO */}
        <Dialog
          open={observacoesDialog}
          onClose={() => setObservacoesDialog(false)}
          maxWidth="md"
          fullWidth
          fullScreen={isSmallMobile}
        >
          <DialogTitle>Adicionar Observações</DialogTitle>
          <DialogContent>
            {selectedMonografia && (
              <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2">
                  {selectedMonografia.projeto?.proposta?.titulo || 'Título não disponível'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Estudante: {selectedMonografia.projeto?.proposta?.estudante?.nome || 'N/A'}
                </Typography>
              </Box>
            )}
            <TextField
              autoFocus
              margin="dense"
              label="Observações"
              fullWidth
              multiline
              rows={isMobile ? 3 : 4}
              variant="outlined"
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              placeholder="Digite suas observações sobre a monografia..."
            />
          </DialogContent>
          <DialogActions sx={{ flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 1 : 0 }}>
            <Button 
              onClick={() => setObservacoesDialog(false)}
              fullWidth={isMobile}
            >
              Cancelar
            </Button>
            <Button
              onClick={executeMonografiaAction}
              variant="contained"
              disabled={actionLoading || !observacoes.trim()}
              startIcon={actionLoading ? <CircularProgress size={16} /> : <SaveIcon />}
              fullWidth={isMobile}
            >
              {actionLoading ? 'Salvando...' : 'Salvar e Devolver'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* DIÁLOGO AGENDAMENTO DEFESA */}
        <Dialog
          open={defesaDialog}
          onClose={() => setDefesaDialog(false)}
          maxWidth="md"
          fullWidth
          fullScreen={isSmallMobile}
        >
          <DialogTitle>Agendar Defesa</DialogTitle>
          <DialogContent>
            {selectedDefesa && (
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2">
                  {selectedDefesa.monografia?.projeto?.proposta?.titulo || 'Título não disponível'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Estudante: {selectedDefesa.monografia?.projeto?.proposta?.estudante?.nome || 'N/A'}
                </Typography>
              </Box>
            )}
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Data e Hora da Defesa"
                  type="datetime-local"
                  value={defesaData.dataDefesa}
                  onChange={(e) => setDefesaData({...defesaData, dataDefesa: e.target.value})}
                  InputLabelProps={{ shrink: true }}
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Local da Defesa"
                  value={defesaData.localDefesa}
                  onChange={(e) => setDefesaData({...defesaData, localDefesa: e.target.value})}
                  placeholder="Ex: Sala 101, Auditório Principal"
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                  <InputLabel>Presidente do Juri</InputLabel>
                  <Select
                    value={defesaData.presidenteId}
                    onChange={(e) => setDefesaData({...defesaData, presidenteId: e.target.value})}
                    label="Presidente da Banca"
                  >
                    <MenuItem value="">Selecione...</MenuItem>
                    <MenuItem value="1">Prof. Dr. João Silva</MenuItem>
                    <MenuItem value="2">Prof. Dra. Maria Santos</MenuItem>
                    <MenuItem value="3">Prof. Dr. Carlos Oliveira</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                  <InputLabel>Arguente</InputLabel>
                  <Select
                    value={defesaData.arguenteId}
                    onChange={(e) => setDefesaData({...defesaData, arguenteId: e.target.value})}
                    label="Arguente"
                  >
                    <MenuItem value="">Selecione...</MenuItem>
                    <MenuItem value="4">Prof. Dr. Ana Costa</MenuItem>
                    <MenuItem value="5">Prof. Dr. Pedro Lima</MenuItem>
                    <MenuItem value="6">Prof. Dra. Lucia Fernandes</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 1 : 0 }}>
            <Button 
              onClick={() => setDefesaDialog(false)}
              fullWidth={isMobile}
            >
              Cancelar
            </Button>
            <Button
              onClick={executeDefesaAction}
              variant="contained"
              disabled={actionLoading || !defesaData.dataDefesa || !defesaData.localDefesa}
              startIcon={actionLoading ? <CircularProgress size={16} /> : <EventIcon />}
              fullWidth={isMobile}
            >
              {actionLoading ? 'Agendando...' : 'Agendar Defesa'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* DIÁLOGO AVALIAÇÃO */}
        <Dialog
          open={avaliacaoDialog}
          onClose={() => setAvaliacaoDialog(false)}
          maxWidth="md"
          fullWidth
          fullScreen={isSmallMobile}
        >
          <DialogTitle>Registar Avaliação</DialogTitle>
          <DialogContent>
            {selectedDefesa && (
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2">
                  {selectedDefesa.monografia?.projeto?.proposta?.titulo || 'Título não disponível'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Estudante: {selectedDefesa.monografia?.projeto?.proposta?.estudante?.nome || 'N/A'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Data da Defesa: {selectedDefesa.data_defesa ? format(new Date(selectedDefesa.data_defesa), 'dd/MM/yyyy HH:mm', { locale: ptBR }) : 'N/A'}
                </Typography>
              </Box>
            )}
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nota do Trabalho Escrito"
                  type="number"
                  inputProps={{ min: 0, max: 20, step: 0.1 }}
                  value={avaliacaoData.notaTrabalhoEscrito}
                  onChange={(e) => setAvaliacaoData({...avaliacaoData, notaTrabalhoEscrito: e.target.value})}
                  helperText="Nota de 0 a 20"
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nota da Defesa Oral"
                  type="number"
                  inputProps={{ min: 0, max: 20, step: 0.1 }}
                  value={avaliacaoData.notaDefesa}
                  onChange={(e) => setAvaliacaoData({...avaliacaoData, notaDefesa: e.target.value})}
                  helperText="Nota de 0 a 20"
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
              <Grid item xs={12}>
                {avaliacaoData.notaTrabalhoEscrito && avaliacaoData.notaDefesa && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      <strong>Média Final:</strong> {((parseFloat(avaliacaoData.notaTrabalhoEscrito) + parseFloat(avaliacaoData.notaDefesa)) / 2).toFixed(2)}
                    </Typography>
                  </Alert>
                )}
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Observações"
                  multiline
                  rows={isMobile ? 3 : 4}
                  value={avaliacaoData.observacoes}
                  onChange={(e) => setAvaliacaoData({...avaliacaoData, observacoes: e.target.value})}
                   placeholder="Comentários sobre a avaliação..."
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 1 : 0 }}>
            <Button 
              onClick={() => setAvaliacaoDialog(false)}
              fullWidth={isMobile}
            >
              Cancelar
            </Button>
            <Button
              onClick={executeDefesaAction}
              variant="contained"
              disabled={actionLoading || !avaliacaoData.notaTrabalhoEscrito || !avaliacaoData.notaDefesa}
              startIcon={actionLoading ? <CircularProgress size={16} /> : <GradeIcon />}
              fullWidth={isMobile}
            >
              {actionLoading ? 'Registrando...' : 'Registrar Avaliação'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* DIÁLOGO DE CHAT */}
        <ChatDialog
          open={chatDialogOpen}
          onClose={closeChatDialogHandler}
          context={chatContext}
          contextId={selectedItem?.id || 0}
          currentUser={user?.nome}
          title={
            selectedItem && selectedItem.proposta
              ? `Chat com ${selectedItem.proposta.estudante.nome}`
              : selectedItem?.projeto?.proposta?.estudante?.nome
              ? `Chat com ${selectedItem.projeto.proposta.estudante.nome}`
              : selectedItem?.proposta?.estudante?.nome
              ? `Chat com ${selectedItem.proposta.estudante.nome}`
              : `Chat - ${chatContext}`
          }
          messages={chatHistory}
          loading={loadingChat}
          onSendMessage={sendChatMessageHandler}
          message={chatMessage}
          onMessageChange={(e) => setChatMessage(e.target.value)}
          onRefresh={async () => {
            if (selectedItem) {
              setLoadingChat(true);
              showNotification('Atualizando chat...', 'info');
              try {
                const response = await api.get('/chat/messages', {
                  params: { context: chatContext, context_id: selectedItem.id }
                });
                setChatHistory(response.data);
                showNotification('Chat atualizado!', 'success');
              } catch (error) {
                console.error("Erro ao atualizar histórico de chat:", error);
                handleApiError(error, 'atualização do chat');
              } finally {
                setLoadingChat(false);
              }
            }
          }}
          showNotification={showNotification}
        />

        {/* DIÁLOGO DE HISTÓRICO - MOBILE OPTIMIZED */}
        <Dialog
          open={historicoDialogOpen}
          onClose={() => setHistoricoDialogOpen(false)}
          maxWidth="lg"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            pb: 1
          }}>
            <Typography variant={isMobile ? "h6" : "h5"}>
              Histórico Completo da Monografia
            </Typography>
            {isMobile && (
              <IconButton
                edge="end"
                onClick={() => setHistoricoDialogOpen(false)}
                size="small"
              >
                <CloseIcon />
              </IconButton>
            )}
          </DialogTitle>
          <DialogContent>
            {historicoData ? (
              <Box>
                {/* Informações gerais - MOBILE OPTIMIZED */}
                <Box sx={{ 
                  mb: 3, 
                  p: isMobile ? 2 : 3, 
                  bgcolor: 'grey.50', 
                  borderRadius: 2, 
                  border: '1px solid', 
                  borderColor: 'grey.200' 
                }}>
                  <Typography 
                    variant={isMobile ? "subtitle1" : "h6"} 
                    sx={{ 
                      mb: 2, 
                      fontWeight: 'bold', 
                      color: 'primary.main',
                      wordBreak: 'break-word'
                    }}
                  >
                    {historicoData?.monografia?.titulo || 'Título não disponível'}
                  </Typography>
                  
                  <Grid container spacing={isMobile ? 1 : 2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <strong>Estudante:</strong> {historicoData?.monografia?.estudante?.nome || 'Nome não disponível'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <strong>Status:</strong> {getStatusDisplayName(historicoData?.monografia?.status)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <strong>Versão atual:</strong> {historicoData?.monografia?.versao_atual || 'Versão não disponível'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <strong>Total de resubmissões:</strong> {historicoData?.monografia?.numero_resubmissoes || 0}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                {/* Timeline - MOBILE OPTIMIZED */}
                <Typography 
                  variant={isMobile ? "subtitle1" : "h6"} 
                  gutterBottom 
                  sx={{ fontWeight: 'bold', color: 'primary.main' }}
                >
                  Timeline de Ações
                </Typography>
                
                {historicoData?.timeline && Array.isArray(historicoData.timeline) && historicoData.timeline.length > 0 ? (
                  <Box sx={{ position: 'relative' }}>
                    {/* Linha vertical da timeline - ajustada para mobile */}
                    {!isMobile && (
                      <Box sx={{
                        position: 'absolute',
                        left: 20,
                        top: 0,
                        bottom: 0,
                        width: 2,
                        bgcolor: 'primary.main',
                        opacity: 0.3
                      }} />
                    )}
                    
                    {historicoData.timeline.map((item, index) => (
                      <Box 
                        key={index} 
                        sx={{ 
                          mb: 3, 
                          ml: isMobile ? 0 : 5,
                          p: isMobile ? 1.5 : 2, 
                          border: 1, 
                          borderColor: 'grey.300', 
                          borderRadius: 2,
                          position: 'relative',
                          bgcolor: 'background.paper',
                          boxShadow: 1,
                          '&:hover': {
                            boxShadow: isMobile ? 1 : 3,
                            borderColor: 'primary.main'
                          }
                        }}
                      >
                        {/* Ponto da timeline - ajustado para mobile */}
                        {!isMobile && (
                          <Box sx={{
                            position: 'absolute',
                            left: -25,
                            top: 12,
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            bgcolor: item?.acao === 'SUBMISSAO_INICIAL' ? 'success.main' :
                                     item?.acao === 'RESUBMISSAO' ? 'warning.main' :
                                     item?.acao?.includes('APROVACAO') ? 'success.main' :
                                     item?.acao?.includes('CORRECOES') ? 'error.main' :
                                     'primary.main',
                            border: '2px solid white',
                            boxShadow: 2
                          }} />
                        )}

                        {/* Cabeçalho da ação - MOBILE OPTIMIZED */}
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: isMobile ? 'flex-start' : 'center', 
                          mb: 2,
                          flexDirection: isMobile ? 'column' : 'row',
                          gap: isMobile ? 1 : 0
                        }}>
                          <Typography 
                            variant={isMobile ? "body1" : "subtitle1"} 
                            sx={{ 
                              fontWeight: 'bold', 
                              color: 'primary.main',
                              wordBreak: 'break-word'
                            }}
                          >
                            {traduzirAcao(item?.acao)}
                          </Typography>
                          <Chip 
                            label={item?.data ? format(new Date(item.data), 'dd/MM/yyyy HH:mm', { locale: ptBR }) : 'Data não disponível'}
                            size="small"
                            variant="outlined"
                            color="primary"
                          />
                        </Box>
                        
                        {/* Versão */}
                        <Typography 
                          variant="body2" 
                          color="primary.main" 
                          sx={{ mb: 1, fontWeight: 'medium' }}
                        >
                          Versão: {item?.versao || 'N/A'}
                        </Typography>

                        {/* Observações */}
                        {item?.dados?.observacoes && (
                          <Box sx={{ 
                            mt: 2, 
                            p: isMobile ? 1.5 : 2, 
                            bgcolor: 'grey.50', 
                            borderRadius: 1,
                            borderLeft: '4px solid',
                            borderLeftColor: 'info.main'
                          }}>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontStyle: 'italic',
                                wordBreak: 'break-word'
                              }}
                            >
                              {item.dados.observacoes}
                            </Typography>
                          </Box>
                        )}

                        {/* Detalhes específicos - MOBILE OPTIMIZED */}
                        <Box sx={{ 
                          mt: 2, 
                          display: 'flex', 
                          flexDirection: 'column', 
                          gap: 1 
                        }}>
                          {item?.dados?.documento && (
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: 'text.secondary',
                                wordBreak: 'break-word'
                              }}
                            >
                              <strong>Documento:</strong> {item.dados.documento}
                            </Typography>
                          )}

                          {item?.dados?.numero_resubmissao && (
                            <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 'medium' }}>
                              <strong>Resubmissão #{item.dados.numero_resubmissao}</strong>
                            </Typography>
                          )}

                          {item?.dados?.versao_documento && (
                            <Typography variant="caption" sx={{ color: 'info.main' }}>
                              <strong>Versão do documento:</strong> {item.dados.versao_documento}
                            </Typography>
                          )}

                          {item?.dados?.prazo && (
                            <Typography variant="caption" sx={{ 
                              color: new Date(item.dados.prazo) < new Date() ? 'error.main' : 'warning.main',
                              fontWeight: 'medium'
                            }}>
                              <strong>Prazo:</strong> {format(new Date(item.dados.prazo), 'dd/MM/yyyy', { locale: ptBR })}
                              {new Date(item.dados.prazo) < new Date() && ' (VENCIDO)'}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Alert severity="info" sx={{ my: 3 }}>
                    <AlertTitle>Timeline vazia</AlertTitle>
                    Nenhuma ação foi registrada no histórico desta monografia ainda.
                  </Alert>
                )}

                {/* Estatísticas - MOBILE OPTIMIZED */}
                {historicoData?.estatisticas ? (
                  <Box sx={{ 
                    mt: 4, 
                    p: isMobile ? 2 : 3, 
                    bgcolor: 'primary.50', 
                    borderRadius: 2, 
                    border: '1px solid', 
                    borderColor: 'primary.200' 
                  }}>
                    <Typography 
                      variant={isMobile ? "subtitle1" : "h6"} 
                      gutterBottom 
                      sx={{ fontWeight: 'bold', color: 'primary.main' }}
                    >
                      Estatísticas do Processo
                    </Typography>
                    
                    <Grid container spacing={isMobile ? 2 : 3}>
                      <Grid item xs={12} sm={4}>
                        <Box sx={{ 
                          textAlign: 'center', 
                          p: isMobile ? 1.5 : 2, 
                          bgcolor: 'white', 
                          borderRadius: 1 
                        }}>
                          <Typography 
                            variant={isMobile ? "h5" : "h4"} 
                            color="primary.main" 
                            sx={{ fontWeight: 'bold' }}
                          >
                            {historicoData.estatisticas.total_acoes || 0}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Total de Ações
                          </Typography>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12} sm={4}>
                        <Box sx={{ 
                          textAlign: 'center', 
                          p: isMobile ? 1.5 : 2, 
                          bgcolor: 'white', 
                          borderRadius: 1 
                        }}>
                          <Typography 
                            variant={isMobile ? "h5" : "h4"} 
                            color="warning.main" 
                            sx={{ fontWeight: 'bold' }}
                          >
                            {historicoData.estatisticas.numero_resubmissoes || 0}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Resubmissões
                          </Typography>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12} sm={4}>
                        <Box sx={{ 
                          textAlign: 'center', 
                          p: isMobile ? 1.5 : 2, 
                          bgcolor: 'white', 
                          borderRadius: 1 
                        }}>
                          <Typography 
                            variant={isMobile ? "h5" : "h4"} 
                            color="success.main" 
                            sx={{ fontWeight: 'bold' }}
                          >
                            {historicoData.estatisticas.tempo_total ? 
                              `${Math.ceil(historicoData.estatisticas.tempo_total / (1000 * 60 * 60 * 24))}d` : 
                              '0d'
                            }
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Tempo Total
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                ) : (
                  <Alert severity="warning" sx={{ mt: 3 }}>
                    <AlertTitle>Estatísticas indisponíveis</AlertTitle>
                    Não foi possível carregar as estatísticas para esta monografia.
                  </Alert>
                )}
              </Box>
            ) : (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                minHeight: isMobile ? 200 : 300 
              }}>
                <Box sx={{ textAlign: 'center' }}>
                  <CircularProgress size={50} />
                  <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
                    Carregando histórico completo...
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                    Por favor, aguarde alguns instantes
                  </Typography>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ 
            p: isMobile ? 2 : 3, 
            bgcolor: 'grey.50',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 1 : 0
          }}>
            <Button 
              onClick={() => setHistoricoDialogOpen(false)}
              variant="contained"
              color="primary"
              size={isMobile ? "medium" : "large"}
              fullWidth={isMobile}
            >
              Fechar
            </Button>
          </DialogActions>
        </Dialog>

        {/* BACKDROP LOADING */}
        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={actionLoading}
        >
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            gap: 2,
            textAlign: 'center',
            p: 3
          }}>
            <CircularProgress color="inherit" />
            <Typography variant={isMobile ? "body1" : "h6"}>
              Processando solicitação...
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                textAlign: 'center', 
                maxWidth: isMobile ? 250 : 300,
                px: 2
              }}
            >
              Por favor, aguarde enquanto processamos sua solicitação.
            </Typography>
          </Box>
        </Backdrop>

        {/* SISTEMA DE NOTIFICAÇÕES */}
        <NotificationSnackbar
          open={notification.open}
          message={notification.message}
          severity={notification.severity}
          onClose={handleCloseNotification}
        />

        {/* Footer - apenas em desktop */}
        {!isMobile && <Footer />}
      </Container>
    </Box>
  );
};

export default SupervisorDashboardPage;